import { useState } from 'react';
import { motion } from 'motion/react';
import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import { useOrgs } from './OrgProvider.js';

/** Shown when the signed-in, allowed user belongs to no organisation yet — see
 * docs/PLAN.md §14 item 3: an allowed domain gets you into the app, an org gets you working. */
export function CreateOrgScreen() {
  const { refresh } = useOrgs();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/orgs', { name: name.trim() });
      await refresh();
    } catch {
      setError('Could not create the organisation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg p-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="w-full max-w-sm"
      >
        <Card className="p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <h1 className="text-lg font-semibold">Create your organisation</h1>
              <p className="mt-1 text-sm text-muted">
                You'll be the owner. You can invite teammates once it's set up.
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="org-name">Organisation name</Label>
              <Input
                id="org-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Organisation name"
                className="h-10"
              />
            </div>
            {error && <p className="text-sm text-danger">{error}</p>}
            <Button
              type="submit"
              variant="default"
              disabled={submitting || !name.trim()}
              className="h-10 justify-center"
            >
              {submitting ? 'Creating…' : 'Create organisation'}
            </Button>
          </form>
        </Card>
      </motion.div>
    </div>
  );
}
